import React, { useCallback, useEffect, useState } from 'react';
import { Check, X, Trash2, RefreshCw, Eraser, Pencil, Save } from 'lucide-react';
import {
    knowledgeExamplesService,
    KnowledgeTermExample,
} from '../services/KnowledgeExamplesService';
import { adminService } from '../services/AdminService';

interface OrgOption { id: number; name: string; }

// Mirrors KnowledgeRulesTab — the `code` MUST be the runtime client_language
// value the backend produces (lowercased; Mexican Spanish is `es-mx`). `*` is the
// language wildcard.
const LANGUAGE_OPTIONS: { code: string; label: string }[] = [
    { code: '*', label: 'All languages' },
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Español' },
    { code: 'es-mx', label: 'Español (México)' },
    { code: 'es-cl', label: 'Español (Chile)' },
    { code: 'es-ec', label: 'Español (Ecuador)' },
    { code: 'pt', label: 'Português' },
];

type ApprovalFilter = 'pending' | 'approved' | 'all';

const KnowledgeExamplesTab: React.FC = () => {
    const [examples, setExamples] = useState<KnowledgeTermExample[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [editing, setEditing] = useState<KnowledgeTermExample | null>(null);
    const [editForm, setEditForm] = useState<{ term: string; part_name: string; part_id: string }>({ term: '', part_name: '', part_id: '' });
    const [saving, setSaving] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);

    const [filterCatalog, setFilterCatalog] = useState('');
    const [filterLanguage, setFilterLanguage] = useState('');
    const [filterTerm, setFilterTerm] = useState('');
    const [filterScope, setFilterScope] = useState<'all' | 'global' | string>('all');
    const [approval, setApproval] = useState<ApprovalFilter>('pending'); // review queue by default
    const [minHits, setMinHits] = useState(1);

    const [orgs, setOrgs] = useState<OrgOption[]>([]);
    const orgName = useCallback(
        (id?: number | null) => (id == null ? 'Global' : (orgs.find(o => o.id === id)?.name || `Org ${id}`)),
        [orgs],
    );

    const fetchExamples = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await knowledgeExamplesService.list({
                catalog: filterCatalog || undefined,
                language: filterLanguage || undefined,
                term: filterTerm || undefined,
                global_only: filterScope === 'global' ? true : undefined,
                organization_id: filterScope !== 'all' && filterScope !== 'global' ? Number(filterScope) : undefined,
                approved: approval === 'all' ? undefined : approval === 'approved',
                min_hits: minHits > 1 ? minHits : undefined,
            });
            setExamples(res.data.examples || []);
            setSelectedIds(new Set());
        } catch (e: any) {
            setError(e?.response?.data?.detail || 'Failed to load knowledge examples');
        } finally {
            setLoading(false);
        }
    }, [filterCatalog, filterLanguage, filterTerm, filterScope, approval, minHits]);

    useEffect(() => { fetchExamples(); }, [fetchExamples]);

    useEffect(() => {
        adminService.getOrganizations()
            .then(res => setOrgs((res.data.organizations || []).map((o: any) => ({ id: o.id, name: o.name }))))
            .catch(() => { /* org list is optional for the table */ });
    }, []);

    const setApprovalState = async (ex: KnowledgeTermExample, approve: boolean) => {
        setBusyId(ex.id); setError(null); setNotice(null);
        try {
            if (approve) await knowledgeExamplesService.approve(ex.id);
            else await knowledgeExamplesService.unapprove(ex.id);
            await fetchExamples();
        } catch (e: any) {
            setError(e?.response?.data?.detail || 'Failed to update approval');
        } finally {
            setBusyId(null);
        }
    };

    const openEdit = (ex: KnowledgeTermExample) => {
        setEditing(ex);
        setEditForm({ term: ex.term, part_name: ex.part_name, part_id: ex.part_id || '' });
    };
    const closeEdit = () => { setEditing(null); };
    const saveEdit = async () => {
        if (!editing) return;
        if (!editForm.term.trim() || !editForm.part_name.trim()) {
            setError('Term and Catalog match are required.');
            return;
        }
        setSaving(true); setError(null);
        try {
            await knowledgeExamplesService.update(editing.id, {
                term: editForm.term.trim(),
                part_name: editForm.part_name.trim(),
                part_id: editForm.part_id.trim() || null as any,
            });
            closeEdit();
            await fetchExamples();
        } catch (e: any) {
            setError(e?.response?.data?.detail || 'Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    const saveEditAndApprove = async () => {
        if (!editing) return;
        if (!editForm.term.trim() || !editForm.part_name.trim()) {
            setError('Term and Catalog match are required.');
            return;
        }
        setSaving(true); setError(null);
        try {
            await knowledgeExamplesService.update(editing.id, {
                term: editForm.term.trim(),
                part_name: editForm.part_name.trim(),
                part_id: editForm.part_id.trim() || null as any,
            });
            await knowledgeExamplesService.approve(editing.id);
            closeEdit();
            await fetchExamples();
        } catch (e: any) {
            setError(e?.response?.data?.detail || 'Failed to save & approve');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (ex: KnowledgeTermExample) => {
        if (!window.confirm(`Delete the example "${ex.term}" → "${ex.part_name}"?`)) return;
        setBusyId(ex.id); setError(null);
        try {
            await knowledgeExamplesService.remove(ex.id);
            await fetchExamples();
        } catch (e: any) {
            setError(e?.response?.data?.detail || 'Failed to delete example');
        } finally {
            setBusyId(null);
        }
    };

    const allSelected = examples.length > 0 && examples.every(ex => selectedIds.has(ex.id));
    const toggleOne = (id: number) => setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(examples.map(ex => ex.id)));

    const deleteSelected = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        if (!window.confirm(`Delete ${ids.length} selected example${ids.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
        setBulkBusy(true); setError(null); setNotice(null);
        try {
            const { data } = await knowledgeExamplesService.bulkRemove(ids);
            setNotice(`Deleted ${data.deleted} example${data.deleted === 1 ? '' : 's'}.`);
            await fetchExamples();
        } catch (e: any) {
            setError(e?.response?.data?.detail || 'Failed to delete selected examples');
        } finally {
            setBulkBusy(false);
        }
    };

    const pruneLog = async () => {
        if (!window.confirm('Prune the raw observation log? Approved examples are NOT affected.')) return;
        setError(null); setNotice(null);
        try {
            const { data } = await knowledgeExamplesService.pruneObservations();
            setNotice(`Pruned ${data.deleted} observation(s) older than ${data.retention_days} days.`);
        } catch (e: any) {
            setError(e?.response?.data?.detail || 'Failed to prune observation log');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900">Catalog Knowledge Examples</h2>
                    <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                        Auto-captured <strong>(request → catalog match)</strong> pairs observed during real
                        searches: when a user asked for a <em>term</em>, the catalog returned the part shown.
                        Approve the good ones — only <strong>approved</strong> examples are compiled into the
                        OE search agent's prompt as proven anchors. Changes take effect on the next search.
                    </p>
                </div>
                <div className="flex gap-2">
                    {selectedIds.size > 0 && (
                        <button onClick={deleteSelected} disabled={bulkBusy} title="Delete the selected examples" className="flex items-center px-3 py-2 text-sm border border-red-200 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50">
                            <Trash2 className="w-4 h-4 mr-2" /> {bulkBusy ? 'Deleting…' : `Delete selected (${selectedIds.size})`}
                        </button>
                    )}
                    <button onClick={pruneLog} title="Delete old raw observations (approved examples untouched)" className="flex items-center px-3 py-2 text-sm border rounded-lg text-gray-700 hover:bg-gray-50">
                        <Eraser className="w-4 h-4 mr-2" /> Prune log
                    </button>
                    <button onClick={fetchExamples} className="flex items-center px-3 py-2 text-sm border rounded-lg text-gray-700 hover:bg-gray-50">
                        <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
                <input value={filterCatalog} onChange={e => setFilterCatalog(e.target.value)} placeholder="Filter catalog" className="px-3 py-2 text-sm border rounded-lg" />
                <select value={filterLanguage} onChange={e => setFilterLanguage(e.target.value)} className="px-3 py-2 text-sm border rounded-lg">
                    <option value="">Any language</option>
                    {LANGUAGE_OPTIONS.map(l => <option key={l.code} value={l.code}>{l.label} ({l.code})</option>)}
                </select>
                <input value={filterTerm} onChange={e => setFilterTerm(e.target.value)} placeholder="Filter term" className="px-3 py-2 text-sm border rounded-lg" />
                <select value={filterScope} onChange={e => setFilterScope(e.target.value)} className="px-3 py-2 text-sm border rounded-lg">
                    <option value="all">All scopes</option>
                    <option value="global">Global only</option>
                    {orgs.map(o => <option key={o.id} value={String(o.id)}>{o.name}</option>)}
                </select>
                <select value={approval} onChange={e => setApproval(e.target.value as ApprovalFilter)} className="px-3 py-2 text-sm border rounded-lg">
                    <option value="pending">Pending review</option>
                    <option value="approved">Approved</option>
                    <option value="all">All</option>
                </select>
                <label className="flex items-center gap-2 text-sm text-gray-600">
                    Min seen
                    <input type="number" min={1} value={minHits} onChange={e => setMinHits(Math.max(1, Number(e.target.value) || 1))} className="w-16 px-2 py-2 text-sm border rounded-lg" />
                </label>
            </div>

            {notice && <div className="px-4 py-3 bg-green-50 text-green-700 text-sm rounded-lg">{notice}</div>}
            {error && <div className="px-4 py-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}

            {/* Table */}
            <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr className="text-left text-gray-500">
                            <th className="px-4 py-2 font-medium w-8">
                                <input
                                    type="checkbox"
                                    aria-label="Select all"
                                    title={allSelected ? 'Deselect all' : 'Select all'}
                                    checked={allSelected}
                                    onChange={toggleAll}
                                    disabled={loading || examples.length === 0}
                                    className="align-middle"
                                />
                            </th>
                            <th className="px-4 py-2 font-medium">Catalog</th>
                            <th className="px-4 py-2 font-medium">Lang</th>
                            <th className="px-4 py-2 font-medium">Scope</th>
                            <th className="px-4 py-2 font-medium">Term</th>
                            <th className="px-4 py-2 font-medium">Catalog match</th>
                            <th className="px-4 py-2 font-medium text-right">Seen</th>
                            <th className="px-4 py-2 font-medium text-right">Score</th>
                            <th className="px-4 py-2 font-medium">Status</th>
                            <th className="px-4 py-2 font-medium text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                        {loading && (
                            <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
                        )}
                        {!loading && examples.length === 0 && (
                            <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">
                                No examples yet — they appear once searches run with the flywheel enabled (KNOWLEDGE_FLYWHEEL=true).
                            </td></tr>
                        )}
                        {!loading && examples.map(ex => (
                            <tr key={ex.id} className={`hover:bg-gray-50 ${selectedIds.has(ex.id) ? 'bg-blue-50/50' : ''}`}>
                                <td className="px-4 py-2">
                                    <input
                                        type="checkbox"
                                        aria-label={`Select ${ex.term}`}
                                        checked={selectedIds.has(ex.id)}
                                        onChange={() => toggleOne(ex.id)}
                                        className="align-middle"
                                    />
                                </td>
                                <td className="px-4 py-2 font-mono text-xs">{ex.catalog}</td>
                                <td className="px-4 py-2">{ex.language}</td>
                                <td className="px-4 py-2">
                                    <span className={`px-2 py-0.5 rounded-full text-xs ${ex.organization_id == null ? 'bg-gray-100 text-gray-600' : 'bg-purple-50 text-purple-700'}`}>
                                        {orgName(ex.organization_id)}
                                    </span>
                                </td>
                                <td className="px-4 py-2 font-medium text-gray-900">{ex.term}</td>
                                <td className="px-4 py-2 text-gray-700">
                                    {ex.part_name}
                                    {ex.part_id && <span className="text-xs text-gray-400 ml-1">({ex.part_id})</span>}
                                </td>
                                <td className="px-4 py-2 text-right text-gray-700">{ex.hit_count}×</td>
                                <td className="px-4 py-2 text-right text-gray-700">{ex.avg_score != null ? `~${ex.avg_score.toFixed(2)}` : '—'}</td>
                                <td className="px-4 py-2">
                                    {ex.approved
                                        ? <span className="px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700">Approved</span>
                                        : <span className="px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700">Pending</span>}
                                </td>
                                <td className="px-4 py-2 text-right whitespace-nowrap">
                                    <button onClick={() => openEdit(ex)} disabled={busyId === ex.id} title="Edit row" className="p-1.5 text-gray-500 hover:text-blue-600 disabled:opacity-40"><Pencil className="w-4 h-4" /></button>
                                    {ex.approved ? (
                                        <button onClick={() => setApprovalState(ex, false)} disabled={busyId === ex.id} title="Revoke approval" className="p-1.5 text-gray-500 hover:text-amber-600 disabled:opacity-40"><X className="w-4 h-4" /></button>
                                    ) : (
                                        <button onClick={() => setApprovalState(ex, true)} disabled={busyId === ex.id} title="Approve into search prompt" className="p-1.5 text-gray-500 hover:text-green-600 disabled:opacity-40"><Check className="w-4 h-4" /></button>
                                    )}
                                    <button onClick={() => remove(ex)} disabled={busyId === ex.id} title="Delete example" className="p-1.5 text-gray-500 hover:text-red-600 disabled:opacity-40"><Trash2 className="w-4 h-4" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Edit modal */}
            {editing && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
                        <div className="flex items-center justify-between px-5 py-4 border-b">
                            <h3 className="font-semibold text-gray-900">Edit example</h3>
                            <button onClick={closeEdit} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-5 space-y-4">
                            <label className="text-sm block">
                                <span className="text-gray-600">Term (original searched text)</span>
                                <input value={editForm.term} onChange={e => setEditForm(f => ({ ...f, term: e.target.value }))} className="mt-1 w-full px-3 py-2 border rounded-lg" placeholder="Para-choques trás (spoiler)" />
                            </label>
                            <label className="text-sm block">
                                <span className="text-gray-600">Catalog match (OE description)</span>
                                <input value={editForm.part_name} onChange={e => setEditForm(f => ({ ...f, part_name: e.target.value }))} className="mt-1 w-full px-3 py-2 border rounded-lg" placeholder="CONJUNTO TAPA-PARACHOQUES TRASERO" />
                            </label>
                            <label className="text-sm block">
                                <span className="text-gray-600">OE number (optional)</span>
                                <input value={editForm.part_id} onChange={e => setEditForm(f => ({ ...f, part_id: e.target.value }))} className="mt-1 w-full px-3 py-2 border rounded-lg" placeholder="86610AO010" />
                            </label>
                            <p className="text-xs text-gray-400">
                                Scope: <span className="font-mono">{editing.catalog}</span> / {editing.language}
                                {editing.avg_score != null ? ` · seen ${editing.hit_count}× · ~${editing.avg_score.toFixed(2)}` : ''}
                            </p>
                        </div>
                        <div className="flex justify-end gap-2 px-5 py-4 border-t">
                            <button onClick={closeEdit} className="px-4 py-2 text-sm border rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                            <button onClick={saveEdit} disabled={saving} className="flex items-center px-4 py-2 text-sm border rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save'}
                            </button>
                            {!editing.approved && (
                                <button onClick={saveEditAndApprove} disabled={saving} className="flex items-center px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                                    <Check className="w-4 h-4 mr-2" /> Save & approve
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KnowledgeExamplesTab;
